import React, {ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {Configuration} from "../../../types/Configurations";
import * as http from "../../../handlers/http";
import ZeeroLoading from "../../custom/ZeeroLoading";
import ZeeroCheck from "../../custom/ZeeroCheck";
import ZeeroButton from "../../custom/ZeeroButton";
import {faSkullCrossbones, faWarning} from "@awesome.me/kit-92187fb46c/icons/classic/solid";
import {ColumnDef, ZeeroDataTable} from "../../custom/ZeeroDataTable";
import ZeeroInlineTextCell from "../../custom/ZeeroInlineTextCell";
import {toast} from "../../custom/toasters/ToastersManager";
import {useTranslation} from "react-i18next";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

export default function GlobalSettingsPage(): ReactElement {
	const {t} = useTranslation();
	const [loading, setLoading] = useState(true);
	const [settings, setSettings] = useState<Configuration[]>([]);
	const [cellEdits, setCellEdits] = useState<Record<string, Record<string, any>>>({});

	useEffect(() => {
		void load();
	}, []);

	const load = useCallback(async () => {
		try {
			const res = await http.get<{ result: Configuration[] }>("/web/admin/global-settings");
			setSettings(res.result);

			const edits: Record<string, Record<string, any>> = {};
			res.result.forEach((c) => {
				edits[c.paramKey] = {Value: c.paramValue};
			});
			setCellEdits(edits);
		} catch (e) {
			toast.error(t("globalSettings_errorLoading"))
		} finally {
			setLoading(false);
		}
	}, []);

	const updatedKeys = useMemo(() => {
		const set = new Set<string>();
		settings.forEach((c) => {
			const current = cellEdits[c.paramKey]?.Value ?? c.paramValue;
			if (current !== c.paramValue) {
				set.add(c.paramKey);
			}
		});
		return set;
	}, [settings, cellEdits]);

	const updates = useMemo(() => {
		return settings
				.filter((c) => updatedKeys.has(c.paramKey))
				.map((c) => ({
					paramKey: c.paramKey,
					paramValue: (cellEdits[c.paramKey]?.Value ?? null) as string | null,
				}))
	}, [settings, cellEdits, updatedKeys]);

	const handleValueChange = (rowId: string, columnId: string, next: any) => {
		setCellEdits((prev) => ({
			...prev,
			[rowId]: {...(prev[rowId] ?? {}), [columnId]: next},
		}));
	};

	const handleDiscard = () => {
		const reset: Record<string, Record<string, any>> = {};
		settings.forEach((c) => (reset[c.paramKey] = {Value: c.paramValue}));
		setCellEdits(reset);
	};

	const handleSave = async () => {
		if (updates.length === 0) {
			return;
		}

		try {
			await http.post("/web/admin/global-settings", updates);
			toast.info(t("globalSettings_savedCorrectly", {count: updates.length}));
		} catch (e) {
			toast.error(t("globalSettings_errorSaving", {count: updates.length}));
		} finally {
			await load();
		}
	};

	if (loading) {
		return (
				<div className="page-content">
					<div className="page-title">{t("globalSettings_globalSettings")}</div>
					<ZeeroLoading className="configurations" message={t("globalSettings_loading")}/>
				</div>
		);
	}

	const columns: ColumnDef<Configuration>[] = [
		{
			id: "Key",
			header: t("general_key"),
			getValue: (c) => c.paramKey,
			cell: ({row}) => <span>{row.paramKey}</span>,
			size: "20%"
		},
		{
			id: "Value",
			header: t("general_value"),
			getValue: (c) => c.paramValue,
			cell: ({row, value, onChange}) => {
				if (row.type === "BOOLEAN") {
					return (
							<ZeeroCheck
									id={`config-check-${row.paramKey}`}
									checked={value === "true"}
									onChange={(checked) => onChange(String(checked))}
							/>
					);
				}

				if (row.type === "STRING") {
					return <ZeeroInlineTextCell value={value} secret={row.secret} onChange={onChange}
												original={row.paramValue}/>;
				}

				return <span>{String(value ?? "")}</span>;
			},
			size: "35%"
		},
		{
			id: "Description",
			header: t("general_description"),
			getValue: (c) => c.description,
			cell: ({row}) => (
					<React.Fragment>
						{row.deprecated && (
								<span style={{marginRight: "0.25em"}}
									  title={t("globalSettings_deprecatedSetting")}>
              							<FontAwesomeIcon icon={faSkullCrossbones} color="var(--warn)"/>
            					</span>
						)}
						{row.description}
					</React.Fragment>
			),
			size: "45%"
		},
	];

	const isSaveEnabled = updates.length > 0;

	return (
			<div className="page-content">
				<div className="page-title">{t("globalSettings_globalSettings")}</div>
				<div className="global-settings-warning">
					<FontAwesomeIcon icon={faWarning} color="var(--warn)"/>
					<strong> {t("general_warning")}</strong>: {t("globalSettings_updateWarning")}
				</div>
				<div className="global-settings-warning">
					<FontAwesomeIcon icon={faWarning} color="var(--warn)"/>
					<strong> {t("general_warning")}</strong>: {t("globalSettings_restartSystem")}
				</div>

				<ZeeroDataTable<Configuration>
						rows={settings}
						getRowId={(c) => c.paramKey}
						columns={columns}
						cellEdits={cellEdits}
						onValueChange={handleValueChange}
						getRowClassName={(row, rowId) => (updatedKeys.has(rowId) ? "updated" : "")}
				/>

				<div className="data-footer actions">
					<ZeeroButton style={{width: "7.5em"}} enabled={isSaveEnabled} type="secondary" onClick={handleDiscard}>
						{t("general_reset")}
					</ZeeroButton>
					<ZeeroButton style={{width: "7.5em"}} enabled={isSaveEnabled} type="primary" onClick={handleSave}>
						{t("general_save")}
					</ZeeroButton>
				</div>
			</div>
	);
}
